// FundContext.js
import React, { createContext, useState, useEffect } from "react";
import styled from "styled-components";
import { exampleTablesData } from "./exampleTablesData";
import xRayHelper from "./portfolio-analysis-helper/portfolio-analysis-helper";
import generateFundComponentStructure from "./FundComponentStructure";
import AssetAllocationMappings from "@/components/FundSingle/portfolio-analysis-helper/mappings/asset-allocation-mapping-emea.json";
import StockSectorsMapping from "@/components/FundSingle/portfolio-analysis-helper/mappings/stock-sectors-mapping.json";

import BasicMaterialIcon from "@/assets/stock-sectors/sectorBasicMaterials.svg";
import ConsumerCyclicalIcon from "@/assets/stock-sectors/superSectorCyclical.svg";
import ConsumerDefensiveIcon from "@/assets/branch-consumer-stable.svg";
import EnergyIcon from "@/assets/stock-sectors/sectorEnergy.svg";
import FinancialIcon from "@/assets/stock-sectors/sectorFinancialServices.svg";
import HealthcareIcon from "@/assets/branch-health.svg";
import IndustrialsIcon from "@/assets/branch-industry.svg";
import RealEstateIcon from "@/assets/branch-real-estate.svg";
import TechnologyIcon from "@/assets/stock-sectors/sectorTechnology.svg";
import CommunicationServicesIcon from "@/assets/stock-sectors/sectorCommunicationServices.svg";
import UtiliesIcon from "@/assets/stock-sectors/sectorUtilities.svg";
import superSectorCyclicalIcon from "@/assets/stock-sectors/superSectorCyclical.svg";

// Create an icon mapping object
const iconMapping = {
  sectorBasicMaterials: BasicMaterialIcon,
  superSectorCyclical: ConsumerCyclicalIcon,
  sectorConsumerDefensive: ConsumerDefensiveIcon,
  sectorEnergy: EnergyIcon,
  sectorFinancialServices: FinancialIcon,
  sectorHealthcare: HealthcareIcon,
  sectorIndustrials: IndustrialsIcon,
  sectorRealEstate: RealEstateIcon,
  sectorTechnology: TechnologyIcon,
  sectorCommunicationServices: CommunicationServicesIcon,
  sectorUtilities: UtiliesIcon,
  superSectorCyclicalIcon: superSectorCyclicalIcon,
};

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  margin-top: 20px;
`;

const LoaderSpinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left: 4px solid darkblue;
  border-radius: 50%;
  width: 70px;
  height: 70px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }

    100% {
      transform: rotate(360deg);
    }
  }
`;

export interface FundContextType {
  securityId?: string;
  fundRiskDetails?: any;
  fundCompareBenchmarkData?: any;
  portfolio?: any;
  largestHoldings?: any;
  stockRegionData?: any;
  fundAllocationBreakdown?: any;
  fundName?: string;
  currentTabIndex: number;
  currentTabStructure?: FundComponentStructureInterface;
  fundComponentDataStructures: FundComponentStructureInterface[];
  hasInitCharts?: boolean;
  tableData: any;
  externalLinkMorningstarMethod?: string;
  setCurrentTabIndex: (index: number) => void;
  getIndustryBySectorCode?: (sectorCode: string) => string;
  getIndustryIconBySectorCode?: (sectorCode: string) => string;
  getBranchNameByTypeId?: (typeId: string) => string;
  getBranchIconByTypeId?: (typeId: string) => string;
  getApiData?: (api: string, params: any) => any;
  outputPercentage?: (percentage: number) => string;
}

export interface FundProviderInterface {
  children: React.ReactNode;
  morningstarId?: string;
  externalLinkMorningstarMethod?: string;
}

const FundContext = createContext<FundContextType>({
  securityId: "",
  fundRiskDetails: {},
  fundCompareBenchmarkData: {},
  portfolio: {},
  largestHoldings: [],
  stockRegionData: {},
  fundAllocationBreakdown: {},
  fundName: "",
  currentTabIndex: 0,
  currentTabStructure: [],
  fundComponentDataStructures: [],
  hasInitCharts: false,
  tableData: {},
  externalLinkMorningstarMethod: "",
  setCurrentTabIndex: () => {},
  getIndustryBySectorCode: () => "",
  getIndustryIconBySectorCode: () => "",
  getBranchNameByTypeId: () => "",
  getBranchIconByTypeId: () => "",
  getApiData: () => {},
  outputPercentage: () => "",
});

// Create a Provider component
export const FundProvider: React.FC<FundProviderInterface> = ({
  children,
  morningstarId,
  externalLinkMorningstarMethod,
}) => {
  // First, check that morningstarId is set
  if (!morningstarId) {
    return <div>No morningstarId set</div>;
  }

  const [hasInitCharts, setHasInitCharts] = useState(false);
  const [securityId, setSecurityId] = useState(morningstarId);
  const [fundRiskDetails, setFundRiskDetails] = useState({});
  const [fundCompareBenchmarkData, setFundCompareBenchmarkData] = useState({});
  const [portfolio, setPortfolio] = useState({});
  const [largestHoldings, setLargestHoldings] = useState([]);
  const [stockRegionData, setStockRegionData] = useState({});
  const [fundName, setFundName] = useState("...");
  const fundComponentDataStructures = generateFundComponentStructure();

  // Check if window object is available
  if (typeof window === "undefined") {
    return (
      <LoaderWrapper>
        <LoaderSpinner />
      </LoaderWrapper>
    );
  }

  var initCurrentTabIndex = 0;

  let urlParams;
  if (typeof window !== "undefined" && window.location) {
    urlParams = new URLSearchParams(window.location.search);
  } else {
    // Provide a fallback or handle the case when it's not in the browser
    urlParams = new URLSearchParams(""); // Empty string as a fallback
  }

  var currentTab = urlParams.get("tab");
  if (currentTab) {
    var tabIndex = fundComponentDataStructures.findIndex(
      (tab) => tab.slug === currentTab
    );
    if (tabIndex >= 0) {
      initCurrentTabIndex = tabIndex;
    }
  }

  const [currentTabIndex, setCurrentTabIndex] = useState(initCurrentTabIndex);
  const currentTabData = fundComponentDataStructures[currentTabIndex];

  // Get example data for the tables for now
  var tableData = exampleTablesData;

  function getIndustryBySectorCode(sectorCode: string) {
    if (StockSectorsMapping.stocks[parseInt(sectorCode)]) {
      return StockSectorsMapping.stocks[parseInt(sectorCode)];
    }
    return "";
  }

  function getIndustryIconBySectorCode(sectorCode: string) {
    if (StockSectorsMapping.stockSectors[parseInt(sectorCode)]) {
      const iconName =
        StockSectorsMapping.stockSectors[parseInt(sectorCode)].icon;
      const icon = iconMapping[iconName];
      if (icon) {
        return icon;
      } else {
        console.warn(`Icon not found for sector code: ${sectorCode}`);
        return null;
      }
    }
    return "";
  }

  const outputPercentage = (percentage: number) => {
    if (typeof percentage === "string") {
      percentage = parseFloat(percentage);
    }

    if (isNaN(percentage)) {
      return "-";
    }

    return percentage.toFixed(2) + "%";
  };

  function getBranchNameByTypeId(typeId: string) {
    if (StockSectorsMapping.stockSectors[typeId]) {
      return StockSectorsMapping.stockSectors[typeId].name;
    }
    return "";
  }

  function getBranchIconByTypeId(typeId: string) {
    if (StockSectorsMapping.stockSectors[typeId]) {
      try {
        const icon = require(
          `@/assets/${StockSectorsMapping.stockSectors[typeId].icon}.svg`
        );
        return icon.default;
      } catch (error) {
        console.warn(
          `Icon not found: ${StockSectorsMapping.stockSectors[typeId].icon}`
        );
        return null;
      }
    }
    return "";
  }

  useEffect(() => {
    var securityDetailsUrl = `${process.env.GATSBY_WP_API_URL}/qte-morningstar/v1/security-details`;
    securityDetailsUrl += "?id=" + securityId;

    fetch(securityDetailsUrl)
      .then((response) => response.json())
      .then((data) => {
        var securityDetails = data[0];
        setFundRiskDetails(securityDetails);
        setFundName(securityDetails.LegalName);
        var Portfolio = securityDetails.Portfolios[0];
        setPortfolio(Portfolio);
        setLargestHoldings(Portfolio.PortfolioHoldings);
      });

    // Load chart.js script. We do it this way instead of using
    // a package manager to solve compatibility issues.
    const chartJSScript = document.createElement("script");
    chartJSScript.src = "https://cdn.jsdelivr.net/npm/chart.js";
    chartJSScript.async = true;
    chartJSScript.onload = () => {
      // Load chartjs-chart-geo script. Used for creating geo charts
      const chartGeoScript = document.createElement("script");
      chartGeoScript.src =
        "https://cdn.jsdelivr.net/npm/chartjs-chart-geo@4.3.4/build/index.umd.min.js";
      chartGeoScript.async = true;

      chartGeoScript.onload = () => {
        const event = new Event("chartjsLoaded");
        window.dispatchEvent(event);
        setHasInitCharts(true);
      };

      document.body.appendChild(chartGeoScript);
    };

    document.body.appendChild(chartJSScript);
  }, []);

  useEffect(() => {
    const tab = fundComponentDataStructures[currentTabIndex];
    window.history.pushState({}, "", `?tab=${tab.slug}`);
  }, [currentTabIndex]);

  return (
    <FundContext.Provider
      value={{
        securityId,
        fundRiskDetails,
        fundCompareBenchmarkData,
        portfolio,
        largestHoldings,
        stockRegionData,
        fundName,
        currentTabIndex,
        currentTabStructure: currentTabData,
        fundComponentDataStructures: fundComponentDataStructures,
        hasInitCharts,
        tableData,
        externalLinkMorningstarMethod,
        setCurrentTabIndex,
        getIndustryBySectorCode,
        getIndustryIconBySectorCode,
        getBranchNameByTypeId,
        getBranchIconByTypeId,
        outputPercentage,
      }}
    >
      {children}
    </FundContext.Provider>
  );
};

export default FundContext;
